import { ComponentChildren, Ref } from 'preact';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { textInterpolations } from '../utils/UNSAFE_interpolations/text';
import type { TextProps } from '../utils/UNSAFE_interpolations/text';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { getLoadingAriaLabel } from './FormControlUtils';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

type AutoFocusProp = HTMLAttributesSignalExcluded<HTMLInputElement>['autofocus'];

type ReadonlyAsInputProps = {
  as: 'input';
  type: HTMLAttributesSignalExcluded<HTMLInputElement>['type'];
  elementRef?: Ref<HTMLInputElement>;
  rows?: never;
};

type ReadonlyAsTextAreaProps = {
  as: 'textarea';
  elementRef?: Ref<HTMLTextAreaElement | HTMLDivElement>;
  type?: never;
  rows?: number;
};

type ReadonlyAsDivProps = {
  as?: 'div';
  elementRef?: Ref<HTMLTextAreaElement | HTMLDivElement>;
  type?: never;
  rows?: never;
};

type Props = TextProps &
  (ReadonlyAsDivProps | ReadonlyAsInputProps | ReadonlyAsTextAreaProps) & {
    ariaDescribedBy?: string;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    autoFocus?: AutoFocusProp;
    hasInsideLabel?: boolean;
    inlineUserAssistance?: ComponentChildren;
    innerReadonlyField?: ComponentChildren;
    value?: string;
    variant?: 'textarea';
  };

// In readonly textarea, we need some margin between the border container element
// and the textarea, so the the textarea's focus outline doesn't get truncated.
// The 0.25rem is an aesthetically pleasing amount of margin to make the readonly
// focus ring visible.
const borderToTextAreaContentMargin = '0.25rem';

const readOnlyTextFieldStyles = {
  base: cssProps`${{
    minHeight:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-typography-body-md-line-height) * var(--oj-c-PRIVATE-DO-NOT-USE-typography-body-md-font-size))',
    // TODO: Use Text component instead of CSS
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    // TODO: Cleanup the border and background CSS
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    borderWidth: '0',
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-primary)',
    boxSizing: 'border-box',
    // TODO: Use the Flex component instead of CSS
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    fontFamily: 'inherit',
    fontSize: '100%',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    outlineWidth: '0',

    '&:focus': {
      outlineStyle: 'dotted',
      outlineWidth: '1px',
      outlineColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-focus-border-color)',
      outlineOffset: '1px'
    }
  }}`,
  textareaBase: cssProps`${{
    resize: 'none',
    paddingBlockStart: '0',
    paddingInlineStart: '0'
  }}`,
  inEnabledFormBase: cssProps`${{
    whiteSpace: 'nowrap',
    overflowX: 'scroll',
    overflowY: 'hidden',
    scrollbarWidth: 'none'
  }}`,

  textareaInEnabledFormBase: cssProps`${{
    marginTop:
      'calc((var(--oj-c-PRIVATE-DO-NOT-USE-text-field-height) - 1rem * var(--oj-c-PRIVATE-DO-NOT-USE-typography-body-md-line-height))/ 2 - 1px)',
    marginBottom:
      'calc((var(--oj-c-PRIVATE-DO-NOT-USE-text-field-height) - 1rem * var(--oj-c-PRIVATE-DO-NOT-USE-typography-body-md-line-height))/ 2 - 1px)',
    marginLeft: borderToTextAreaContentMargin,
    marginRight: borderToTextAreaContentMargin,
    paddingLeft: `calc(var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding) - ${borderToTextAreaContentMargin})`,
    paddingRight: '0',
    overflowX: 'auto',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap'
  }}`,

  inEnabledFormLabelInside: cssProps`${{
    // TODO: Look into why we need this hacky CSS
    marginTop:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-text-field-inside-edge-padding-top) + 0.1875rem)',
    paddingTop: '0',
    minHeight:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-typography-body-md-line-height) * var(--oj-c-PRIVATE-DO-NOT-USE-typography-body-md-font-size))'
  }}`,

  textareaInEnabledFormLabelInside: cssProps`${{
    paddingTop: 0,
    paddingBottom: 0,
    // We are using a transparent border here instead of padding, due to how textarea scrolls the padding off the screen
    // and the text content will clash with the inside label.
    borderColor: 'transparent',
    borderStyle: 'solid',
    marginTop:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-font-size) * var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-line-height) + var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-position-top))',
    marginBottom: borderToTextAreaContentMargin
  }}`,

  inEnabledFormNoLabelInside: cssProps`${{
    // TODO: Remove if this is not needed.
    justifyContent: 'center'
  }}`,

  // don't apply this for textarea
  inEnabledFormNoStartContent: cssProps`${{
    marginLeft: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding)',
    marginRight: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding)',
    paddingLeft: '0',
    paddingRight: '0'
  }}`
};

export function ReadonlyTextFieldInput({
  ariaDescribedBy,
  ariaLabel,
  ariaLabelledBy,
  autoFocus,
  as = 'div',
  elementRef,
  hasInsideLabel = false,
  inlineUserAssistance,
  innerReadonlyField,
  rows,
  type,
  value = '',
  variant,
  ...props
}: Props) {
  const { isFormLayout, isReadonly: isReadonlyForm } = useFormContext();
  const { isLoading } = useFormFieldContext();
  const ariaLabelForReadonly = isLoading ? getLoadingAriaLabel() : ariaLabel;

  // get classes for text align
  const interpolations = [...Object.values(textInterpolations)];
  const styleInterpolations = mergeInterpolations<Props>(interpolations);
  const { class: styleInterpolationClasses } = styleInterpolations(props);

  const readonlyDivClasses = classNames([
    as !== 'textarea' && 'oj-c-hide-scrollbar', // TODO: Temp workaround for using --webkit-scrollbar to hide the scrollbar
    readOnlyTextFieldStyles.base,
    as === 'textarea' && readOnlyTextFieldStyles.textareaBase,
    isFormLayout && !isReadonlyForm && readOnlyTextFieldStyles.inEnabledFormBase,
    variant === 'textarea' &&
      isFormLayout &&
      !isReadonlyForm &&
      readOnlyTextFieldStyles.textareaInEnabledFormBase,
    isFormLayout &&
      !isReadonlyForm &&
      (hasInsideLabel
        ? readOnlyTextFieldStyles.inEnabledFormLabelInside
        : readOnlyTextFieldStyles.inEnabledFormNoLabelInside),
    variant === 'textarea' &&
      isFormLayout &&
      !isReadonlyForm &&
      hasInsideLabel &&
      readOnlyTextFieldStyles.textareaInEnabledFormLabelInside,
    variant !== 'textarea' && // not for textarea
      isFormLayout &&
      !isReadonlyForm &&
      readOnlyTextFieldStyles.inEnabledFormNoStartContent,
    styleInterpolationClasses
  ]);

  // JET-52914 - Implement Tabbable mode API contract in InputText
  // spread tabbableModeProps on component
  const { isTabbable, tabbableModeProps } = useTabbableMode();

  if (as === 'input') {
    return (
      <input
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabelForReadonly}
        aria-labelledby={ariaLabelledBy}
        autofocus={autoFocus}
        class={readonlyDivClasses}
        readonly={true}
        // @ts-ignore
        ref={elementRef}
        type={type}
        value={value}
        {...(!isTabbable && tabbableModeProps)}
      />
    );
  }
  if (as === 'textarea') {
    return (
      <textarea
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabelForReadonly}
        aria-labelledby={ariaLabelledBy}
        autofocus={autoFocus}
        class={readonlyDivClasses}
        readonly={true}
        // @ts-ignore
        ref={elementRef}
        rows={rows}
        {...(!isTabbable && tabbableModeProps)}>
        {value}
      </textarea>
    );
  }
  return (
    <div
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabelForReadonly}
      aria-labelledby={ariaLabelledBy}
      aria-readonly={true}
      autofocus={autoFocus}
      class={readonlyDivClasses}
      // @ts-ignore
      ref={elementRef}
      role="textbox"
      {...tabbableModeProps}>
      {value}
    </div>
  );
}
